import { createContext } from "react";
import { instanceOf, node } from "prop-types";
import { observable, action, computed } from "mobx";
import {
  DEFAULT_WORK_TYPE_COLOR,
  DEFAULT_WORK_TYPES,
  PROJECT_STATUS,
  simplifyTextString
} from "@utils";

//-------------------------------------------------------------------------------------------
export class ActiveProjectStore {

  @observable accessor projects = [];
  @observable accessor workTypes = [];
  @observable accessor projectWorkTypes = [];
  @observable accessor template;
  @observable accessor currentProjectUuid;
  @observable accessor useAdvancedAISettings = false;
  
  // @observable accessor files = [];
  @observable accessor xlsProjectStructure;
  @observable accessor xlsFileName;
  
  
  
  // =============== COMPUTED-S ===============
  
  @computed get activeWorkTypes() {
    if(!this.currentProjectUuid) return;
    return this.workTypes.filter((wT) => wT.isActive);
  }
  
  @computed get currentProject() {
    if(!this.currentProjectUuid) return;
    return this.projects.find((project) => project.uuid === this.currentProjectUuid);
  }
  
  @computed get availableWorkTypes() {
    return this.workTypes.filter(wT => (
      !wT.hidden || this.projectWorkTypes.includes(wT.id)
    ));
  }
  
  @computed get hasXlsFile() {
    return Boolean(this.xlsProjectStructure && this.xlsFileName);
  }
  
  // =============== PROJECT ACTIONS ===============
  
  @action setCurrentProject(currentProjectUuid) {
    this.currentProjectUuid = currentProjectUuid;
  }

  @action setProjects(projects) {
    this.projects = projects;
  }
  
  @action setWorkTypes(workTypes) {
    this.workTypes = workTypes;
  }
  
  @action addWorkType(workType) {
    const existing = this.workTypes.find(wT => wT.id === workType.id || simplifyTextString(wT.name) === simplifyTextString(workType.name));
    if(!existing)
      this.workTypes.push(workType);
    
    return existing || workType;
  }
  
  @action addCompanyWorkType(workType) {
    this.workTypes.push(workType);
  }
  
  @action removeWorkType(workType) {
    this.workTypes = this.workTypes.filter((w) => w.id !== workType || !w.custom);
  }
  
  @action updateWorkTypeData(id, name, backgroundColor) {
    const l = [...this.workTypes];
    const match = l.find(wT => wT.id === id);
    if(!match)
      return;
    
    match.name = name;
    match.backgroundColor = backgroundColor;
    this.workTypes = l;
  }
  
  @action setBtProjectsStatus(jiraProjects) {
    jiraProjects.forEach(({ apropoId, setUp, mapping }) => {
      const p = this.projects.find(p => p.id === apropoId);
      if(p) {
        p.btExported = mapping !== null;
        p.btSetUp = Boolean(setUp);
      }
    })
  }
  
  @action addOrUpdateProject(projectData) {
    let tmp = [...this.projects];
    let existingIdx = tmp.findIndex((p) => p.uuid === projectData.uuid);
    if(existingIdx >= 0)
      tmp[existingIdx] = projectData;
    else
      tmp.push(projectData);
    
    this.projects = tmp;
  }
  
  
  @action removeProject(uuid) {
    this.projects = this.projects.filter((pr) => pr.uuid !== uuid);
  }
  
  @action updateLocalProject(uuid, data) {
    const project = this.projects.find((p) => p.uuid === uuid);
    Object.keys(data).forEach((key) => {
      project[key] = data[key];
    });
  }

  @action editLocalProjectData(uuid, action, data) {
    try {
      let tmp = [...this.projects];
      let project = tmp.find((p) => p.uuid === uuid);

      if(!project)
        return;

      switch (action) {
        case "attachmentAdd":
          project.attachments.push({
            ...data,
            type: "attachment",
          });
          break;
        case "attachmentRemove":
          project.attachments = project.attachments.filter(
            (a) => a.uuid !== data
          );
          break;
        case "status":
          project.status = data.status;
          project.order = data.order;
          // status, order, sourceStatus, sourceOrder
          tmp.forEach((pr) => {
            if(pr.uuid === project.uuid)
              return;
            if(pr.status === data.sourceStatus && pr.order >= data.sourceOrder) {
              pr.order--; // fill the gap
            }
            if(pr.status === data.status && pr.order >= data.order) {
              pr.order++; //give space for new item
            }
          });
          break;
        case "observers":
          project.users.observer = data;
          break;
        case "owner":
          project.users.owner = data;
          break;
        case "archive":
        case "remove":
          tmp = tmp.filter((pr) => pr.uuid !== uuid);
          break;
        default:
          project[action] = data;
      }
      this.projects = tmp;
    } catch(e) {
      console.error(e);
    }
  }
  
  
  // =============== CREATOR ACTIONS ===============
  @action clearProjectData() {
    this.projectWorkTypes = [];
    this.template = undefined;
    this.useAdvancedAISettings = false;
  }
  
  @action addWorkTypes(workTypes) {
    const ids = this.workTypes
      .filter(wT => !wT.custom)
      .map(w => w.id);
    this.workTypes = [
      ...this.workTypes,
      ...workTypes
        .filter(wT => !ids.includes(wT.id))
        .sort((a,b) => Math.abs(a.id) > Math.abs(b.id) ? 1 : -1)
        .map((wT) => ({
          // id: wT.id < 0 ? wT.id : -Math.abs(new Date().getTime()+index),
          id: wT.id,
          name: wT.name,
          template: true,
          backgroundColor: wT.backgroundColor || DEFAULT_WORK_TYPE_COLOR
        }))
    ];
  }
  
  status = PROJECT_STATUS.TODO;
  setStatus = (status) => {
    this.status = status;
  };
  
  @action setProjectWorkTypes(workTypes) {
    this.projectWorkTypes = workTypes;
  }
  @action setDefaultProjectWorkTypes() {
    this.projectWorkTypes = DEFAULT_WORK_TYPES
      .filter(wId => this.workTypes.find(wT => wT.id === wId && !wT.hidden));
  }
  
  @action setTemplate(template, company=false) {
    this.template = template
      ? {...template, company}
      : undefined;
    this.xlsProjectStructure = undefined;
    this.xlsFileName = undefined;
    
    let wTSet = false;
    
    if(template?.metadata) {
      const templateWorkTypes = JSON.parse(template.metadata)?.workTypes || [];
      if(templateWorkTypes?.length) {
        this.addWorkTypes(templateWorkTypes)
        this.setProjectWorkTypes(templateWorkTypes?.map(mWT => mWT.id));
        wTSet = true;
      }
    } else {
      this.setDefaultProjectWorkTypes();
    }
    
    return wTSet;
  }
  
  // @action setFiles(files) {
  //   this.files = files;
  // }
  
  @action setXlsProjectStructure(structure, fileName) {
    this.xlsProjectStructure = structure;
    this.xlsFileName = fileName;
  }
  // ========= AI Actions =========
  @action setUseAdvancedAISettings(useAdvancedAISettings) {
    this.useAdvancedAISettings  = useAdvancedAISettings;
  }
}



export const ActiveProjectStoreContext = createContext(null);

export const ActiveProjectStoreProvider = ({ children, value }) => (
  <ActiveProjectStoreContext.Provider value={value}>
    {children}
  </ActiveProjectStoreContext.Provider>
);

ActiveProjectStoreProvider.propTypes = {
  children: node.isRequired,
  value: instanceOf(ActiveProjectStore).isRequired,
};
